// Every customer-facing string lives here. English is the default and the reference; each other
// language must provide every key (enforced by the `Messages` type). Placeholders: {name}.
import { enV2, skV2 } from "./strings-v2";

export const en = {
  "app.title": "Secure checkout",
  "common.skip": "Skip to content",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.copiedAria": "{label} copied to clipboard",
  "common.close": "Close",
  "common.retry": "Try again",
  "common.loading": "Loading payment…",
  "common.theme": "Theme",
  "common.themeLight": "Light theme",
  "common.themeDark": "Dark theme",
  "common.language": "Language",
  "common.testnet": "Test network",
  "common.testnetNote": "Test network — no real money moves.",
  "common.viewOnExplorer": "View on explorer",
  "common.invoice": "Invoice",
  "common.nonCustodial": "Non-custodial. Your payment goes straight to {merchant}'s wallet.",

  "error.notFoundTitle": "Payment link not found",
  "error.notFoundBody": "This link may be mistyped or no longer valid. Ask the store for a new payment link.",
  "error.networkTitle": "Can't reach the payment server",
  "error.networkBody": "Check your connection. If you already paid, your payment is safe on the blockchain and will be matched when the connection is back.",

  "pay.payTo": "Pay {merchant}",
  "pay.amountDue": "Amount due",
  "pay.total": "Order total",
  "pay.network": "Network",
  "pay.expiresIn": "Expires in",
  "pay.expiresSoon": "Expires soon",
  "pay.expiresAria": "Time left to pay: {time}",
  "pay.order": "Order",
  "pay.statusPage": "Payment status page",
  "pay.tabWallet": "Pay with wallet",
  "pay.tabExchange": "Pay from exchange",
  "pay.methodLabel": "Payment method",
  "pay.live": "Live",
  "pay.reconnecting": "Checking every few seconds",

  "wallet.choose": "Choose your wallet",
  "wallet.chooseHint": "You'll confirm the payment in your wallet. Nothing is sent until you approve it.",
  "wallet.detected": "Detected",
  "wallet.install": "Install",
  "wallet.noneFound": "No {chain} wallet found in this browser",
  "wallet.noneFoundHint": "Open this page in your wallet app, or pay from an exchange instead.",
  "wallet.openIn": "Open in {wallet}",
  "wallet.get": "Get {wallet}",
  "wallet.connecting": "Connecting to {wallet}…",
  "wallet.connectingHint": "Approve the connection in your wallet.",
  "wallet.connected": "Connected",
  "wallet.disconnect": "Disconnect",
  "wallet.change": "Change",
  "wallet.payButton": "Pay {amount} {asset}",
  "wallet.preparing": "Preparing payment…",
  "wallet.awaiting": "Confirm in your wallet",
  "wallet.awaitingHint": "Check {wallet} — a request to pay {amount} {asset} is waiting for your approval.",
  "wallet.awaitingStep": "Step {step} of {steps}",
  "wallet.approveStep": "Allow {asset} for this payment",
  "wallet.payStep": "Send the payment",
  "wallet.wrongNetwork": "Your wallet is on another network",
  "wallet.switchNetwork": "Switch to {chain}",
  "wallet.switching": "Switching network…",
  "wallet.breakdown": "One transaction sends {merchantAmount} to {merchant} and a {feeAmount} service fee to the payment operator.",
  "wallet.unavailableTitle": "Wallet payments aren't available here yet",
  "wallet.unavailableBody": "Please pay from an exchange or another wallet using the address instead.",
  "wallet.err.rejected": "You declined the request in your wallet. Nothing was sent.",
  "wallet.err.insufficient": "Your wallet doesn't have enough {asset} (or network fee) for this payment.",
  "wallet.err.wrong_network": "Your wallet is on a different network. Switch to {chain} and try again.",
  "wallet.err.unavailable": "This wallet can't make this payment. Try another wallet.",
  "wallet.err.failed": "The wallet couldn't complete the payment. Nothing was charged.",
  "wallet.errDetail": "Wallet said: {detail}",
  "wallet.feeNote": "Your wallet also charges a small network fee, paid in {gas}.",

  "progress.title": "Payment sent",
  "progress.sent": "Sent from your wallet",
  "progress.detected": "Seen on the {chain} network",
  "progress.confirming": "Confirming",
  "progress.confirmingHint": "Usually takes a few seconds. You can keep this page open or close it — the payment is already on its way.",
  "progress.paid": "Paid",
  "progress.tx": "Transaction",

  "exchange.warningTitle": "Send exactly {amount} {asset}",
  "exchange.warningBody": "Use the {network} network. A different amount or network can't be matched to this order and may be lost.",
  "exchange.amount": "Exact amount",
  "exchange.amountHint": "Includes {extra} {asset} so we can recognise your transfer.",
  "exchange.network": "Network",
  "exchange.address": "{asset} address on {chain}",
  "exchange.scan": "Scan the address with your exchange app",
  "exchange.steps": "How to pay",
  "exchange.step1": "In your exchange, open Withdraw and choose {asset}.",
  "exchange.step2": "Select the {network} network.",
  "exchange.step3": "Paste the address and the exact amount, then confirm.",
  "exchange.feeWarning": "If your exchange deducts its withdrawal fee from the amount, add the fee on top so {amount} {asset} arrives.",
  "exchange.waiting": "Waiting for your transfer",
  "exchange.waitingHint": "This page updates on its own when the payment arrives.",
  "exchange.showQr": "Show QR code",
  "exchange.hideQr": "Hide QR code",

  "status.pending": "Awaiting payment",
  "status.confirming": "Confirming",
  "status.paid": "Paid",
  "status.partially_paid": "Partially paid",
  "pay.partialTitle": "Part of the payment arrived",
  "pay.partialBody": "{received} of {amount} {asset} arrived so far. Wallet pay now sends only the missing {missing} {asset}. The order stays open until the timer ends.",
  "status.underpaid": "Underpaid",
  "status.overpaid": "Paid (overpaid)",
  "status.expired": "Expired",
  "status.flagged": "Under review",

  "done.paidTitle": "Payment received",
  "done.paidBody": "Thank you. {merchant} has received your payment.",
  "done.receipt": "Receipt",
  "done.amountPaid": "Amount paid",
  "done.paidAt": "Paid on",
  "done.method": "Method",
  "done.methodWallet": "Wallet",
  "done.methodExchange": "Exchange transfer",
  "done.returnTo": "Return to {merchant}",
  "done.viewReceipt": "Open receipt page",
  "done.saveHint": "Save the receipt page link — it's your proof of payment.",

  "done.overpaidTitle": "Payment received",
  "done.overpaidBody": "You sent {extra} {asset} more than the order total. Your order is paid. To get the difference back, contact {merchant} and share your receipt page.",
  "done.underpaidTitle": "Payment incomplete",
  "done.underpaidBody": "We received {received} of {amount} {asset} — {missing} {asset} short. Your order isn't paid yet.",
  "done.underpaidNext": "Don't send another transfer on your own. Contact {merchant}, share the status page link below, and they'll tell you how to complete or refund it.",
  "done.expiredTitle": "This payment request expired",
  "done.expiredBody": "No payment arrived in time, so nothing was charged. Return to the store to get a new payment link.",
  "done.expiredLate": "Already sent money? Don't worry — it's on the blockchain. Contact {merchant} with the status page link below.",
  "done.flaggedTitle": "Payment under review",
  "done.flaggedBody": "Your payment arrived and is being reviewed before the order is confirmed. {merchant} will contact you. You don't need to do anything now.",
  "done.contact": "Contact {merchant}",
  "done.backToStore": "Back to store",
  "done.received": "Received",
  "done.expected": "Expected",

  "receipt.title": "Payment status",
  "receipt.subtitle": "Public record of this payment. Share this page with {merchant} if you need help.",
  "receipt.timeline": "Timeline",
  "receipt.created": "Payment request created",
  "receipt.expires": "Payment window closes",
  "receipt.expired": "Payment window closed",
  "receipt.detected": "Transaction detected",
  "receipt.settled": "Payment settled",
  "receipt.evidence": "On-chain evidence",
  "receipt.txHash": "Transaction hash",
  "receipt.payer": "Paid from",
  "receipt.merchant": "Paid to",
  "receipt.token": "Token contract",
  "receipt.reference": "Payment reference",
  "receipt.amountDue": "Amount due",
  "receipt.received": "Amount received",
  "receipt.noTx": "No transaction yet. This page updates automatically.",
  "receipt.openCheckout": "Go to checkout",
  "receipt.verify": "Anyone can verify this payment on the public blockchain explorer.",

  "demo.banner": "DEMO — devnet test tokens, no real money",
  "demo.title": "Demo store",
  "demo.subtitle": "A sample shop that shows the checkout end to end on a test network.",
  "demo.add": "Add to cart",
  "demo.added": "In cart",
  "demo.cart": "Cart",
  "demo.cartEmpty": "Your cart is empty. Add an item to try the checkout.",
  "demo.subtotal": "Total",
  "demo.payOn": "Pay on",
  "demo.checkout": "Checkout",
  "demo.creating": "Creating payment…",
  "demo.createFailed": "Couldn't start the checkout: {message}",
  "demo.qty": "Quantity",
  "demo.increase": "Add one {name}",
  "demo.decrease": "Remove one {name}",
  "demo.remove": "Remove",
  "demo.faucet": "Need test USDC? Circle's public faucet hands out devnet and Sepolia test tokens.",
  "demo.product.notebook": "Dot-grid notebook",
  "demo.product.notebookDesc": "A5, 120 pages, lay-flat binding.",
  "demo.product.mug": "Ceramic mug",
  "demo.product.mugDesc": "350 ml, stoneware, dishwasher safe.",
  "demo.product.tote": "Canvas tote bag",
  "demo.product.toteDesc": "Heavy cotton, 38 × 42 cm.",
  "demo.orderDescription": "Demo order · {count} items",

  "wallet.err.insufficient_token": "Your wallet doesn't have enough {asset} on {chain} for this payment. Nothing was sent.",
  "wallet.err.insufficient_gas": "Your wallet needs a little {gas} to cover the network fee. Add some {gas} and try again — nothing was sent.",
  "wallet.err.simulation": "This payment would fail on the {chain} network, so it wasn't sent to your wallet. Nothing was charged.",
  "wallet.openHint": "On your phone? Open this page inside your wallet app to pay:",
  "wallet.androidApp": "Wallet app on this phone",

  "pay.noMethodsTitle": "No payment method is available right now",
  "pay.noMethodsBody": "Please contact the store, or try again later.",
  "pay.demoBanner": "DEMO payment — test network, no real money",

  "guide.title": "Step by step for your exchange",
  "guide.pick": "Your exchange",
  "guide.other": "Other",
  "guide.open": "Open Withdraw, choose {asset} and paste the address above.",
  "guide.network": "Select the {network} network.",
  "guide.confirm": "Review and confirm the withdrawal.",
  "guide.binance.amount": "Choose “Receive amount” and enter {amount}. Binance then adds its network fee on top, so exactly {amount} {asset} arrives.",
  "guide.okx.open": "Open Withdraw, choose {asset}, pick the on-chain withdrawal (not a transfer to another OKX account) and paste the address above.",
  "guide.okx.amount": "Enter {amount}. Before you confirm, check the amount that will be received: it must be exactly {amount} {asset}. If the fee is taken out of it, raise the amount by the fee.",
  "guide.bybit.amount": "If Bybit lets you choose how the fee is charged, pick the option where the recipient gets the full amount you enter. Enter {amount} and check that exactly {amount} {asset} will be received.",
  "guide.other.amount": "Enter exactly {amount}. If your exchange takes its fee out of the amount, add the fee on top so {amount} {asset} arrives.",
  "exchange.internalTitle": "Withdraw on-chain only",
  "exchange.internalBody": "A transfer between two accounts on the same exchange often never reaches the blockchain, so this page can't detect it.",
  "exchange.minimumBody": "Exchanges set a minimum withdrawal for each network. If {amount} {asset} is below your exchange's minimum, it won't let you send it.",
  "exchange.minimumWallet": "In that case, pay with a wallet instead.",

  "footer.terms": "Terms",
  "footer.privacy": "Privacy",
  "footer.poweredBy": "Powered by {name}",

  "help.button": "Need help?",
  "help.title": "Need help with this payment?",
  "help.email": "Email support",
  "help.site": "Help center",
  "help.reference": "Mention this payment ID",
  "help.subject": "Payment {id}",

  "redirect.in": "Returning to {merchant} in {seconds} s",
  "redirect.announce": "You'll be returned to {merchant} in {seconds} seconds.",
  "redirect.cancel": "Stay here",
  "redirect.cancelled": "Automatic return cancelled.",

  "demo.disabledTitle": "The demo store is turned off",
  "demo.disabledBody": "This checkout isn't running in demo mode. Open the payment link you received from the store.",

  "home.title": "Nothing to pay here",
  "home.body": "Open the payment link you received from the store.",
  "home.demo": "Try the demo store",
  ...enV2,
};

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;

export const sk: Messages = {
  "app.title": "Bezpečná platba",
  "common.skip": "Preskočiť na obsah",
  "common.copy": "Kopírovať",
  "common.copied": "Skopírované",
  "common.copiedAria": "{label} skopírované do schránky",
  "common.close": "Zavrieť",
  "common.retry": "Skúsiť znova",
  "common.loading": "Načítavam platbu…",
  "common.theme": "Vzhľad",
  "common.themeLight": "Svetlý vzhľad",
  "common.themeDark": "Tmavý vzhľad",
  "common.language": "Jazyk",
  "common.testnet": "Testovacia sieť",
  "common.testnetNote": "Testovacia sieť — neprenášajú sa skutočné peniaze.",
  "common.viewOnExplorer": "Zobraziť v prehliadači blockchainu",
  "common.invoice": "Faktúra",
  "common.nonCustodial": "Bez sprostredkovateľa. Platba ide priamo do peňaženky {merchant}.",

  "error.notFoundTitle": "Platobný odkaz sa nenašiel",
  "error.notFoundBody": "Odkaz môže byť nesprávny alebo už neplatí. Požiadajte obchod o nový platobný odkaz.",
  "error.networkTitle": "Platobný server je nedostupný",
  "error.networkBody": "Skontrolujte pripojenie. Ak ste už zaplatili, platba je bezpečne na blockchaine a priradí sa po obnovení spojenia.",

  "pay.payTo": "Zaplatiť {merchant}",
  "pay.amountDue": "Suma na úhradu",
  "pay.total": "Suma objednávky",
  "pay.network": "Sieť",
  "pay.expiresIn": "Platnosť",
  "pay.expiresSoon": "Čoskoro vyprší",
  "pay.expiresAria": "Čas na zaplatenie: {time}",
  "pay.order": "Objednávka",
  "pay.statusPage": "Stav platby",
  "pay.tabWallet": "Zaplatiť peňaženkou",
  "pay.tabExchange": "Zaplatiť z burzy",
  "pay.methodLabel": "Spôsob platby",
  "pay.live": "Naživo",
  "pay.reconnecting": "Kontrolujem každých pár sekúnd",

  "wallet.choose": "Vyberte peňaženku",
  "wallet.chooseHint": "Platbu potvrdíte vo svojej peňaženke. Nič sa neodošle, kým ju neschválite.",
  "wallet.detected": "Nájdená",
  "wallet.install": "Inštalovať",
  "wallet.noneFound": "V tomto prehliadači sa nenašla peňaženka pre {chain}",
  "wallet.noneFoundHint": "Otvorte túto stránku v aplikácii peňaženky alebo zaplaťte z burzy.",
  "wallet.openIn": "Otvoriť v {wallet}",
  "wallet.get": "Získať {wallet}",
  "wallet.connecting": "Pripájam {wallet}…",
  "wallet.connectingHint": "Schváľte pripojenie vo svojej peňaženke.",
  "wallet.connected": "Pripojené",
  "wallet.disconnect": "Odpojiť",
  "wallet.change": "Zmeniť",
  "wallet.payButton": "Zaplatiť {amount} {asset}",
  "wallet.preparing": "Pripravujem platbu…",
  "wallet.awaiting": "Potvrďte v peňaženke",
  "wallet.awaitingHint": "Pozrite sa do {wallet} — čaká tam žiadosť o platbu {amount} {asset}.",
  "wallet.awaitingStep": "Krok {step} z {steps}",
  "wallet.approveStep": "Povoliť {asset} pre túto platbu",
  "wallet.payStep": "Odoslať platbu",
  "wallet.wrongNetwork": "Peňaženka je na inej sieti",
  "wallet.switchNetwork": "Prepnúť na {chain}",
  "wallet.switching": "Prepínam sieť…",
  "wallet.breakdown": "Jedna transakcia pošle {merchantAmount} obchodu {merchant} a servisný poplatok {feeAmount} prevádzkovateľovi platieb.",
  "wallet.unavailableTitle": "Platba peňaženkou tu zatiaľ nie je dostupná",
  "wallet.unavailableBody": "Zaplaťte prosím z burzy alebo inej peňaženky pomocou adresy.",
  "wallet.err.rejected": "Žiadosť ste v peňaženke zamietli. Nič sa neodoslalo.",
  "wallet.err.insufficient": "V peňaženke nie je dosť {asset} (alebo na sieťový poplatok).",
  "wallet.err.wrong_network": "Peňaženka je na inej sieti. Prepnite na {chain} a skúste znova.",
  "wallet.err.unavailable": "Táto peňaženka nevie túto platbu vykonať. Skúste inú.",
  "wallet.err.failed": "Peňaženka platbu nedokončila. Nič sa nestrhlo.",
  "wallet.errDetail": "Správa peňaženky: {detail}",
  "wallet.feeNote": "Peňaženka si účtuje aj malý sieťový poplatok v {gas}.",

  "progress.title": "Platba odoslaná",
  "progress.sent": "Odoslané z vašej peňaženky",
  "progress.detected": "Zachytené v sieti {chain}",
  "progress.confirming": "Potvrdzuje sa",
  "progress.confirmingHint": "Zvyčajne to trvá pár sekúnd. Stránku môžete nechať otvorenú alebo zavrieť — platba je už na ceste.",
  "progress.paid": "Zaplatené",
  "progress.tx": "Transakcia",

  "exchange.warningTitle": "Pošlite presne {amount} {asset}",
  "exchange.warningBody": "Použite sieť {network}. Inú sumu alebo sieť nevieme priradiť k objednávke a platba sa môže stratiť.",
  "exchange.amount": "Presná suma",
  "exchange.amountHint": "Obsahuje {extra} {asset}, aby sme vašu platbu rozpoznali.",
  "exchange.network": "Sieť",
  "exchange.address": "Adresa {asset} v sieti {chain}",
  "exchange.scan": "Naskenujte adresu v aplikácii burzy",
  "exchange.steps": "Ako zaplatiť",
  "exchange.step1": "Na burze otvorte Výber (Withdraw) a zvoľte {asset}.",
  "exchange.step2": "Vyberte sieť {network}.",
  "exchange.step3": "Vložte adresu a presnú sumu a potvrďte.",
  "exchange.feeWarning": "Ak burza strhne poplatok za výber zo sumy, pripočítajte ho, aby dorazilo {amount} {asset}.",
  "exchange.waiting": "Čakáme na vašu platbu",
  "exchange.waitingHint": "Stránka sa sama aktualizuje, keď platba dorazí.",
  "exchange.showQr": "Zobraziť QR kód",
  "exchange.hideQr": "Skryť QR kód",

  "status.pending": "Čaká na platbu",
  "status.confirming": "Potvrdzuje sa",
  "status.paid": "Zaplatené",
  "status.partially_paid": "Čiastočne zaplatená",
  "pay.partialTitle": "Časť platby prišla",
  "pay.partialBody": "Zatiaľ prišlo {received} z {amount} {asset}. Platba peňaženkou teraz pošle len chýbajúcich {missing} {asset}. Objednávka zostáva otvorená, kým neuplynie čas.",
  "status.underpaid": "Nedoplatok",
  "status.overpaid": "Zaplatené (preplatok)",
  "status.expired": "Vypršalo",
  "status.flagged": "V preverovaní",

  "done.paidTitle": "Platba prijatá",
  "done.paidBody": "Ďakujeme. {merchant} dostal vašu platbu.",
  "done.receipt": "Potvrdenie",
  "done.amountPaid": "Zaplatená suma",
  "done.paidAt": "Zaplatené",
  "done.method": "Spôsob",
  "done.methodWallet": "Peňaženka",
  "done.methodExchange": "Prevod z burzy",
  "done.returnTo": "Späť do {merchant}",
  "done.viewReceipt": "Otvoriť potvrdenie",
  "done.saveHint": "Uložte si odkaz na potvrdenie — je to váš doklad o platbe.",

  "done.overpaidTitle": "Platba prijatá",
  "done.overpaidBody": "Poslali ste o {extra} {asset} viac, než je suma objednávky. Objednávka je zaplatená. Pre vrátenie rozdielu kontaktujte {merchant} a pošlite odkaz na potvrdenie.",
  "done.underpaidTitle": "Platba nie je úplná",
  "done.underpaidBody": "Prijali sme {received} z {amount} {asset} — chýba {missing} {asset}. Objednávka zatiaľ nie je zaplatená.",
  "done.underpaidNext": "Neposielajte ďalší prevod na vlastnú päsť. Kontaktujte {merchant}, pošlite odkaz na stav platby nižšie a povedia vám, ako ju dokončiť alebo vrátiť.",
  "done.expiredTitle": "Platobná požiadavka vypršala",
  "done.expiredBody": "Platba nedorazila včas, nič sa nestrhlo. Vráťte sa do obchodu a získajte nový platobný odkaz.",
  "done.expiredLate": "Už ste peniaze poslali? Nebojte sa — sú na blockchaine. Kontaktujte {merchant} s odkazom na stav platby nižšie.",
  "done.flaggedTitle": "Platba sa preveruje",
  "done.flaggedBody": "Platba dorazila a pred potvrdením objednávky sa preveruje. {merchant} vás bude kontaktovať. Teraz nemusíte nič robiť.",
  "done.contact": "Kontaktovať {merchant}",
  "done.backToStore": "Späť do obchodu",
  "done.received": "Prijaté",
  "done.expected": "Očakávané",

  "receipt.title": "Stav platby",
  "receipt.subtitle": "Verejný záznam tejto platby. Ak potrebujete pomoc, pošlite túto stránku {merchant}.",
  "receipt.timeline": "Priebeh",
  "receipt.created": "Platobná požiadavka vytvorená",
  "receipt.expires": "Platnosť platby končí",
  "receipt.expired": "Platnosť platby skončila",
  "receipt.detected": "Transakcia zachytená",
  "receipt.settled": "Platba vysporiadaná",
  "receipt.evidence": "Dôkaz na blockchaine",
  "receipt.txHash": "Hash transakcie",
  "receipt.payer": "Zaplatené z",
  "receipt.merchant": "Zaplatené na",
  "receipt.token": "Kontrakt tokenu",
  "receipt.reference": "Referencia platby",
  "receipt.amountDue": "Suma na úhradu",
  "receipt.received": "Prijatá suma",
  "receipt.noTx": "Zatiaľ žiadna transakcia. Stránka sa aktualizuje automaticky.",
  "receipt.openCheckout": "Prejsť k platbe",
  "receipt.verify": "Túto platbu si môže ktokoľvek overiť vo verejnom prehliadači blockchainu.",

  "demo.banner": "DEMO — testovacie tokeny na devnete, žiadne skutočné peniaze",
  "demo.title": "Demo obchod",
  "demo.subtitle": "Ukážkový obchod, ktorý predvedie celú platbu na testovacej sieti.",
  "demo.add": "Do košíka",
  "demo.added": "V košíku",
  "demo.cart": "Košík",
  "demo.cartEmpty": "Košík je prázdny. Pridajte položku a vyskúšajte platbu.",
  "demo.subtotal": "Spolu",
  "demo.payOn": "Platiť v sieti",
  "demo.checkout": "Pokračovať k platbe",
  "demo.creating": "Vytváram platbu…",
  "demo.createFailed": "Platbu sa nepodarilo spustiť: {message}",
  "demo.qty": "Množstvo",
  "demo.increase": "Pridať jeden kus: {name}",
  "demo.decrease": "Odobrať jeden kus: {name}",
  "demo.remove": "Odstrániť",
  "demo.faucet": "Potrebujete testovacie USDC? Verejný faucet spoločnosti Circle rozdáva testovacie tokeny pre devnet a Sepoliu.",
  "demo.product.notebook": "Zápisník s bodkovou mriežkou",
  "demo.product.notebookDesc": "A5, 120 strán, rozloží sa naplocho.",
  "demo.product.mug": "Keramický hrnček",
  "demo.product.mugDesc": "350 ml, kamenina, do umývačky.",
  "demo.product.tote": "Plátenná taška",
  "demo.product.toteDesc": "Hrubá bavlna, 38 × 42 cm.",
  "demo.orderDescription": "Demo objednávka · {count} ks",

  "wallet.err.insufficient_token": "V peňaženke nie je dosť {asset} v sieti {chain} na túto platbu. Nič sa neodoslalo.",
  "wallet.err.insufficient_gas": "Peňaženka potrebuje trochu {gas} na sieťový poplatok. Doplňte {gas} a skúste znova — nič sa neodoslalo.",
  "wallet.err.simulation": "Táto platba by v sieti {chain} zlyhala, preto sme ju do peňaženky neposlali. Nič sa nestrhlo.",
  "wallet.openHint": "Ste na telefóne? Otvorte túto stránku priamo v aplikácii peňaženky:",
  "wallet.androidApp": "Aplikácia peňaženky v telefóne",

  "pay.noMethodsTitle": "Momentálne nie je dostupný žiadny spôsob platby",
  "pay.noMethodsBody": "Kontaktujte prosím obchod alebo to skúste neskôr.",
  "pay.demoBanner": "DEMO platba — testovacia sieť, žiadne skutočné peniaze",

  "guide.title": "Krok za krokom pre vašu burzu",
  "guide.pick": "Vaša burza",
  "guide.other": "Iná",
  "guide.open": "Otvorte Výber (Withdraw), zvoľte {asset} a vložte adresu vyššie.",
  "guide.network": "Vyberte sieť {network}.",
  "guide.confirm": "Skontrolujte a potvrďte výber.",
  "guide.binance.amount": "Zvoľte „Receive amount“ (suma, ktorú príjemca dostane) a zadajte {amount}. Binance pripočíta sieťový poplatok navrch, takže dorazí presne {amount} {asset}.",
  "guide.okx.open": "Otvorte Výber (Withdraw), zvoľte {asset}, vyberte výber na blockchain (nie prevod na iný účet OKX) a vložte adresu vyššie.",
  "guide.okx.amount": "Zadajte {amount}. Pred potvrdením skontrolujte sumu, ktorá dorazí: musí byť presne {amount} {asset}. Ak sa z nej strháva poplatok, navýšte sumu o poplatok.",
  "guide.bybit.amount": "Ak vám Bybit ponúkne výber, ako sa poplatok účtuje, zvoľte možnosť, pri ktorej príjemca dostane celú zadanú sumu. Zadajte {amount} a skontrolujte, že dorazí presne {amount} {asset}.",
  "guide.other.amount": "Zadajte presne {amount}. Ak burza strhne poplatok zo sumy, pripočítajte ho, aby dorazilo {amount} {asset}.",
  "exchange.internalTitle": "Len výber na blockchain",
  "exchange.internalBody": "Prevod medzi dvoma účtami na tej istej burze sa často na blockchain vôbec nedostane, takže ho táto stránka nevie zachytiť.",
  "exchange.minimumBody": "Burzy majú pre každú sieť minimálnu sumu výberu. Ak je {amount} {asset} pod minimom vašej burzy, výber vám nepovolí.",
  "exchange.minimumWallet": "V tom prípade zaplaťte peňaženkou.",

  "footer.terms": "Podmienky",
  "footer.privacy": "Ochrana súkromia",
  "footer.poweredBy": "Beží na {name}",

  "help.button": "Potrebujete pomoc?",
  "help.title": "Potrebujete pomoc s touto platbou?",
  "help.email": "Napísať podpore",
  "help.site": "Centrum pomoci",
  "help.reference": "Uveďte toto ID platby",
  "help.subject": "Platba {id}",

  "redirect.in": "Návrat do {merchant} o {seconds} s",
  "redirect.announce": "O {seconds} sekúnd sa vrátite do {merchant}.",
  "redirect.cancel": "Zostať tu",
  "redirect.cancelled": "Automatický návrat zrušený.",

  "demo.disabledTitle": "Demo obchod je vypnutý",
  "demo.disabledBody": "Táto platobná brána nebeží v demo režime. Otvorte platobný odkaz, ktorý ste dostali od obchodu.",

  "home.title": "Tu nie je čo platiť",
  "home.body": "Otvorte platobný odkaz, ktorý ste dostali od obchodu.",
  "home.demo": "Vyskúšať demo obchod",
  ...skV2,
};

export const LANGUAGES = { en, sk } as const;
export type Lang = keyof typeof LANGUAGES;
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", sk: "Slovenčina" };
